import { useCallback } from "react";
import { message as antMessage } from "antd/lib";
import { useIntl } from "react-intl";
import type { RcFile } from "antd/es/upload/interface";
import { commonApi } from "../../../../services/api";
import { getBase64 } from "../utils/fileUtils";
import type { SmartCompressor } from "../../../../utils/compressors/SmartCompressor";

interface UseFileHandlersProps {
  smartCompressor: SmartCompressor;
  setImageLoading: (loading: boolean) => void;
  setPreviewBase64: (base64: string | null) => void;
  setUploadedImageUrl: (url: string | null) => void;
  uploadedImageUrlRef: React.MutableRefObject<string | null>;
  setVideoLoading: (loading: boolean) => void;
  setPreviewVideo: (thumbnail: string | null) => void;
  setUploadedVideoUrl: (url: string | null) => void;
  uploadedVideoUrlRef: React.MutableRefObject<string | null>;
  setFileLoading: (loading: boolean) => void;
  setPreviewFile: (file: { name: string; url: string | null } | null) => void;
  setUploadedFileUrl: (url: string | null) => void;
  uploadedFileUrlRef: React.MutableRefObject<string | null>;
  setContent: (content: string) => void;
  contentRef: React.MutableRefObject<string>;
  fileInputRef: React.RefObject<HTMLInputElement>;
}

export const useFileHandlers = ({
  smartCompressor,
  setImageLoading,
  setPreviewBase64,
  setUploadedImageUrl,
  uploadedImageUrlRef,
  setVideoLoading,
  setPreviewVideo,
  setUploadedVideoUrl,
  uploadedVideoUrlRef,
  setFileLoading,
  setPreviewFile,
  setUploadedFileUrl,
  uploadedFileUrlRef,
  setContent,
  contentRef,
  fileInputRef,
}: UseFileHandlersProps) => {
  const intl = useIntl();

  // 清除图片、视频和文件状态
  const clearSelectedMedia = useCallback(() => {
    setPreviewBase64(null);
    setUploadedImageUrl(null);
    uploadedImageUrlRef.current = null;
    setPreviewVideo(null);
    setUploadedVideoUrl(null);
    uploadedVideoUrlRef.current = null;
    setPreviewFile(null);
    setUploadedFileUrl(null);
    uploadedFileUrlRef.current = null;
  }, [
    setPreviewBase64,
    setUploadedImageUrl,
    uploadedImageUrlRef,
    setPreviewVideo,
    setUploadedVideoUrl,
    uploadedVideoUrlRef,
    setPreviewFile,
    setUploadedFileUrl,
    uploadedFileUrlRef,
  ]);

  // 处理文件选择
  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const file = files[0]; // 暂时只处理第一个文件

      // 检查文件类型
      if (file.type.startsWith("image/")) {
        // 处理图片文件
        try {
          setImageLoading(true);

          // 先生成预览（使用原始文件）
          const base64 = await getBase64(file);
          setPreviewBase64(base64);

          // 使用SmartCompressor进行智能压缩
          const compressionResult = await smartCompressor.compress(file);

          // 将blob转换为File对象
          let finalFile = new File([compressionResult.blob], file.name, {
            type: compressionResult.blob.type,
            lastModified: Date.now(),
          });
          const finalSizeMB = finalFile.size / 1024 / 1024;

          if (!compressionResult.success) {
            finalFile = file;
          }

          // 检查最终文件大小
          if (finalSizeMB > 50) {
            antMessage.error(intl.formatMessage({ id: "file.image.size.error" }));
            setPreviewBase64(null);
            setUploadedImageUrl(null);
            uploadedImageUrlRef.current = null;
            return;
          }

          // 创建FormData对象
          const formData = new FormData();
          formData.append("file", finalFile);

          // 上传图片
          const response = await commonApi.uploadImage(formData);
          if (response.success && response.data) {
            setUploadedImageUrl(response.data);
            uploadedImageUrlRef.current = response.data;
            setContent(" ");
          } else {
            antMessage.error(intl.formatMessage({ id: "image.upload.error" }));
            setPreviewBase64(null);
            setUploadedImageUrl(null);
            uploadedImageUrlRef.current = null;
          }
        } catch (error) {
          antMessage.error(intl.formatMessage({ id: "image.process.error" }));
          setPreviewBase64(null);
          setUploadedImageUrl(null);
          uploadedImageUrlRef.current = null;
        } finally {
          setImageLoading(false);
        }
      } else if (file.type.startsWith("video/")) {
        // 处理视频文件
        try {
          setVideoLoading(true);

          // 智能压缩视频
          const compressionResult = await smartCompressor.compress(file);

          // 将blob转换为File对象
          let finalFile = new File([compressionResult.blob], file.name, {
            type: compressionResult.blob.type,
            lastModified: Date.now(),
          });
          const finalSizeMB = finalFile.size / 1024 / 1024;

          if (!compressionResult.success) {
            finalFile = file;
          }

          // 检查最终文件大小
          if (finalSizeMB > 50) {
            antMessage.error(intl.formatMessage({ id: "file.video.size.error" }));
            setPreviewVideo(null);
            setUploadedVideoUrl(null);
            uploadedVideoUrlRef.current = null;
            return;
          }

          // 生成视频缩略图
          const thumbnail = await new Promise<string>((resolve, reject) => {
            const video = document.createElement("video");
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            video.onloadedmetadata = () => {
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
              video.currentTime = 1;
            };

            video.onseeked = () => {
              if (ctx) {
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                resolve(canvas.toDataURL("image/jpeg", 0.8));
              } else {
                reject(
                  new Error(intl.formatMessage({ id: "video.thumbnail.generate.error" }))
                );
              }
            };

            video.onerror = () =>
              reject(new Error(intl.formatMessage({ id: "video.load.error" })));
            video.src = URL.createObjectURL(file);
          });
          setPreviewVideo(thumbnail);

          // 创建FormData对象
          const formData = new FormData();
          formData.append("file", finalFile);

          // 上传视频
          const response = await commonApi.uploadVideo(formData);
          if (response.success && response.data) {
            setUploadedVideoUrl(response.data);
            uploadedVideoUrlRef.current = response.data;
            setContent(" ");
          } else {
            antMessage.error(intl.formatMessage({ id: "video.upload.error" }));
            setPreviewVideo(null);
            setUploadedVideoUrl(null);
            uploadedVideoUrlRef.current = null;
          }
        } catch (error) {
          antMessage.error(intl.formatMessage({ id: "video.process.error" }));
          setPreviewVideo(null);
          setUploadedVideoUrl(null);
          uploadedVideoUrlRef.current = null;
        } finally {
          setVideoLoading(false);
        }
      } else {
        // 处理其他类型的文件
        try {
          setFileLoading(true);

          const fileSizeMB = file.size / 1024 / 1024;

          // 检查文件大小
          if (fileSizeMB > 100) {
            antMessage.error(intl.formatMessage({ id: "file.size.error" }) || "文件大小不能超过100MB");
            return;
          }

          // 设置文件预览
          setPreviewFile({ name: file.name, url: null });

          const formData = new FormData();
          formData.append("file", file);

          // 使用 commonApi.uploadFile 上传文件
          const response = await commonApi.uploadFile(formData);
          if (response.success && response.data) {
            // 后端返回的可能是对象 {fileName, fileUrl} 或直接是 URL 字符串
            const fileUrl = typeof response.data === 'string' 
              ? response.data 
              : response.data.fileUrl || response.data;
            setUploadedFileUrl(fileUrl);
            uploadedFileUrlRef.current = fileUrl;
            setPreviewFile({ name: file.name, url: fileUrl });
            setContent(" ");
          } else {
            antMessage.error(intl.formatMessage({ id: "file.upload.error" }) || "文件上传失败");
            setPreviewFile(null);
            setUploadedFileUrl(null);
            uploadedFileUrlRef.current = null;
          }
        } catch (error) {
          console.error("文件上传失败:", error);
          antMessage.error(intl.formatMessage({ id: "file.upload.error" }) || "文件上传失败");
          setPreviewFile(null);
          setUploadedFileUrl(null);
          uploadedFileUrlRef.current = null;
        } finally {
          setFileLoading(false);
        }
      }

      // 清空input值，允许重复选择同一文件
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [
      smartCompressor,
      intl,
      setImageLoading,
      setPreviewBase64,
      setUploadedImageUrl,
      uploadedImageUrlRef,
      setVideoLoading,
      setPreviewVideo,
      setUploadedVideoUrl,
      uploadedVideoUrlRef,
      setFileLoading,
      setPreviewFile,
      setUploadedFileUrl,
      uploadedFileUrlRef,
      setContent,
      fileInputRef,
    ]
  );

  // 图片上传处理（带压缩功能）
  const handleImageUpload = useCallback(
    async (file: RcFile) => {
      if (!file.type.startsWith("image/")) {
        antMessage.error(intl.formatMessage({ id: "file.image.type.error" }));
        return false;
      }

      try {
        setImageLoading(true);

        const base64 = await getBase64(file);
        setPreviewBase64(base64);

        const compressionResult = await smartCompressor.compress(file);

        let finalFile = new File([compressionResult.blob], file.name, {
          type: compressionResult.blob.type,
          lastModified: Date.now(),
        });
        const finalSizeMB = finalFile.size / 1024 / 1024;

        if (!compressionResult.success) {
          finalFile = file;
        }

        if (finalSizeMB > 50) {
          antMessage.error(intl.formatMessage({ id: "file.image.size.error" }));
          setPreviewBase64(null);
          setUploadedImageUrl(null);
          uploadedImageUrlRef.current = null;
          return false;
        }

        const formData = new FormData();
        formData.append("file", finalFile);

        const response = await commonApi.uploadImage(formData);
        if (response.success && response.data) {
          setUploadedImageUrl(response.data);
          uploadedImageUrlRef.current = response.data;
          setContent(" ");
          return false;
        } else {
          antMessage.error(intl.formatMessage({ id: "image.upload.error" }));
          setPreviewBase64(null);
          setUploadedImageUrl(null);
          uploadedImageUrlRef.current = null;
          return false;
        }
      } catch (error) {
        antMessage.error(intl.formatMessage({ id: "image.process.error" }));
        setPreviewBase64(null);
        setUploadedImageUrl(null);
        uploadedImageUrlRef.current = null;
        return false;
      } finally {
        setImageLoading(false);
      }
    },
    [smartCompressor, intl, setImageLoading, setPreviewBase64, setUploadedImageUrl, uploadedImageUrlRef, setContent]
  );

  // 视频上传处理
  const handleVideoUpload = useCallback(
    async (file: RcFile) => {
      if (!file.type.startsWith("video/")) {
        antMessage.error(intl.formatMessage({ id: "file.video.type.error" }));
        return false;
      }

      try {
        setVideoLoading(true);

        const originalSizeMB = file.size / 1024 / 1024;

        if (originalSizeMB > 100) {
          antMessage.error(intl.formatMessage({ id: "file.video.size.error" }));
          return false;
        }

        // 生成视频缩略图
        const thumbnail = await new Promise<string>((resolve, reject) => {
          const video = document.createElement("video");
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          video.onloadedmetadata = () => {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            video.currentTime = 1;
          };

          video.onseeked = () => {
            if (ctx) {
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              resolve(canvas.toDataURL("image/jpeg", 0.8));
            } else {
              reject(
                new Error(intl.formatMessage({ id: "video.thumbnail.generate.error" }))
              );
            }
          };

          video.onerror = () =>
            reject(new Error(intl.formatMessage({ id: "video.load.error" })));
          video.src = URL.createObjectURL(file);
        });
        setPreviewVideo(thumbnail);

        const formData = new FormData();
        formData.append("file", file);

        const response = await commonApi.uploadVideo(formData);
        if (response.success && response.data) {
          setUploadedVideoUrl(response.data);
          uploadedVideoUrlRef.current = response.data;
          setContent(" ");
          return false;
        } else {
          antMessage.error(intl.formatMessage({ id: "video.upload.error" }));
          setPreviewVideo(null);
          setUploadedVideoUrl(null);
          uploadedVideoUrlRef.current = null;
          return false;
        }
      } catch (error) {
        antMessage.error(intl.formatMessage({ id: "video.process.error" }));
        setPreviewVideo(null);
        setUploadedVideoUrl(null);
        uploadedVideoUrlRef.current = null;
        return false;
      } finally {
        setVideoLoading(false);
      }
    },
    [intl, setVideoLoading, setPreviewVideo, setUploadedVideoUrl, uploadedVideoUrlRef, setContent]
  );

  // 文件上传处理（非图片非视频文件）
  const handleFileUpload = useCallback(
    async (file: RcFile) => {
      // 如果是图片或视频，不应该使用这个函数
      if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
        return false;
      }

      try {
        setFileLoading(true);

        const fileSizeMB = file.size / 1024 / 1024;

        // 检查文件大小（可以根据需要调整限制）
        if (fileSizeMB > 100) {
          antMessage.error(intl.formatMessage({ id: "file.size.error" }) || "文件大小不能超过100MB");
          return false;
        }

        // 设置文件预览
        setPreviewFile({ name: file.name, url: null });

        const formData = new FormData();
        formData.append("file", file);

        // 使用 commonApi.uploadFile 上传文件
        const response = await commonApi.uploadFile(formData);
        if (response.success && response.data) {
          // 后端返回的可能是对象 {fileName, fileUrl} 或直接是 URL 字符串
          const fileUrl = typeof response.data === 'string' 
            ? response.data 
            : response.data.fileUrl || response.data;
          setUploadedFileUrl(fileUrl);
          uploadedFileUrlRef.current = fileUrl;
          setPreviewFile({ name: file.name, url: fileUrl });
          setContent(" ");
          return false;
        } else {
          antMessage.error(intl.formatMessage({ id: "file.upload.error" }) || "文件上传失败");
          setPreviewFile(null);
          setUploadedFileUrl(null);
          uploadedFileUrlRef.current = null;
          return false;
        }
      } catch (error) {
        console.error("文件上传失败:", error);
        antMessage.error(intl.formatMessage({ id: "file.upload.error" }) || "文件上传失败");
        setPreviewFile(null);
        setUploadedFileUrl(null);
        uploadedFileUrlRef.current = null;
        return false;
      } finally {
        setFileLoading(false);
      }
    },
    [intl, setFileLoading, setPreviewFile, setUploadedFileUrl, uploadedFileUrlRef, setContent]
  );

  // 通用文件上传处理函数
  const handleGeneralFileUpload = useCallback(
    async (file: RcFile) => {
      if (file.type.startsWith("image/")) {
        return await handleImageUpload(file);
      } else if (file.type.startsWith("video/")) {
        return await handleVideoUpload(file);
      } else {
        // 对于其他类型的文件，使用文件上传接口
        return await handleFileUpload(file);
      }
    },
    [handleImageUpload, handleVideoUpload, handleFileUpload]
  );

  return {
    clearSelectedMedia,
    handleFileSelect,
    handleImageUpload,
    handleVideoUpload,
    handleFileUpload,
    handleGeneralFileUpload,
  };
};

